import { Link } from 'react-router-dom';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

/**
 * A credibility bridge for the Get EA page.
 *
 * Before this section the page had no route back into the rest of the site, so
 * a cautious visitor had nowhere to go and verify anything. This gives them two
 * honest exits — the live account and the mechanics — without competing with
 * the page's real call to action, which stays "start the chat".
 */
const FACTS = [
  { id: 'live', value: 'Real live account', label: 'Every number comes from an account we trade ourselves' },
  { id: 'record', value: 'Losing months shown', label: 'The full record is published, not a best case' },
  { id: 'rules', value: 'Fixed risk, always', label: 'Hard stops and a set fraction of equity on every trade' },
];

export default function TrustBridge() {
  return (
    <section className="py-14 md:py-20 bg-background-100 border-y border-background-200">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
              <div>
                <Reveal>
                  <SectionHeading
                    align="left"
                    eyebrow="Why trust it"
                    title="The same EA we run on a live account"
                    subtitle="We do not hand out a cut-down demo. What you claim is the exact expert advisor trading our own gold account — and you can check its record before you install anything."
                  />
                </Reveal>

                <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
                  {FACTS.map((fact, index) => (
                    <Reveal key={fact.id} delay={index * 70}>
                      <div className="flex items-start gap-2.5">
                        <span className="mt-1 w-1 h-8 rounded-full bg-accent-400 shrink-0" aria-hidden="true"></span>
                        <div>
                          <p className="font-heading text-sm font-bold text-foreground-950 leading-snug">
                            {fact.value}
                          </p>
                          <p className="mt-1 text-xs leading-snug text-foreground-600">{fact.label}</p>
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>

              <Reveal delay={120}>
                <div className="flex flex-col gap-3">
                  <Link
                    to="/performance"
                    className="inline-flex items-center justify-between gap-3 rounded-lg border border-background-300 bg-background-50 px-5 py-4 hover:border-accent-500 transition-colors duration-200 cursor-pointer group"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                        <i className="ri-line-chart-line text-xl" aria-hidden="true"></i>
                      </span>
                      <span>
                        <span className="block font-heading text-sm font-semibold text-foreground-950">
                          See live performance
                        </span>
                        <span className="block text-xs text-foreground-500">Monthly returns and drawdown</span>
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-lg text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all duration-200" aria-hidden="true"></i>
                  </Link>

                  <Link
                    to="/how-it-works"
                    className="inline-flex items-center justify-between gap-3 rounded-lg border border-background-300 bg-background-50 px-5 py-4 hover:border-accent-500 transition-colors duration-200 cursor-pointer group"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                        <i className="ri-book-open-line text-xl" aria-hidden="true"></i>
                      </span>
                      <span>
                        <span className="block font-heading text-sm font-semibold text-foreground-950">
                          How it works
                        </span>
                        <span className="block text-xs text-foreground-500">Strategy, exits and setup</span>
                      </span>
                    </span>
                    <i className="ri-arrow-right-line text-lg text-foreground-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all duration-200" aria-hidden="true"></i>
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}